import React from 'react';

import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, Link, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useFunctionalities } from 'queries/functionalities';
import { useIncidentRoles } from 'queries/incidentRoles';
import { useStatusPages } from 'queries/nunc/statusPages';
import { useRunbooks } from 'queries/runbooks';
import { usePagingOptions } from 'queries/runbooks/runbookPagingOptions';
import useServices from 'queries/services';
import { useTaskLists } from 'queries/taskLists';
import { useTeams } from 'queries/teams';
import { useTicketingProjects } from 'queries/ticketing/projects';

import Loading from 'components/common/Loading/Loading';
import OrgRouteLink from 'components/common/OrgRouteLink';

const Prerequisite = ({ prereq, integration }) => {
  let met = true;

  const prereqs = {
    team: {
      query: useTeams,
      params: { page: 1, per_page: 1, lite: true },
      metCheck: (data) => !!data.length,
    },
    role: {
      query: useIncidentRoles,
      params: { per_page: 1 },
      metCheck: (data) => !!data.length,
    },
    runbook: {
      query: useRunbooks,
      params: { per_page: 1 },
      metCheck: (data) => !!data.length,
    },
    task_list: {
      query: useTaskLists,
      params: { per_page: 1 },
      metCheck: (data) => !!data.length,
    },
    services_with_functionalities: {
      query: useServices,
      metCheck: (data) => !!data?.data?.data?.length,
    },
    functionalities_with_services: {
      query: useFunctionalities,
      metCheck: (data) => !!data?.data?.data?.length,
    },
    paging: {
      query: usePagingOptions,
      params: { per_page: 1 },
      metCheck: (data) => !!data.length,
    },
    projects: {
      query: useTicketingProjects,
      params: {
        connection_ids: integration.connections?.[0]?.id,
        configured_projects: true,
        page: 1,
      },
      metCheck: (data) => !!data.length,
    },
    status_page: {
      query: useStatusPages,
      params: { per_page: 1 },
      metCheck: (data) => !!data.length,
    },
  };
  const lookupSlug = prereq.slug.startsWith('project')
    ? 'projects'
    : prereq.slug;

  const query = prereqs[lookupSlug].query;
  const params = prereqs[lookupSlug].params || {};
  const metCheck = prereqs[lookupSlug].metCheck;

  const { data, isLoading: metIsLoading } = query(params, {
    enabled: integration.enabled,
    staleTime: 120000,
  });

  met = integration.enabled ? metCheck(data) : false;

  // TODO: Services and functionalities shouldn't show if they have related impacts.
  // Update warning and change API to support this check.

  // setup paths for projects have to be created dynamically with the id
  if (lookupSlug === 'projects' && integration.enabled) {
    prereq.setup_path = `/settings/integrations/shortcut/connections/${integration.connections?.[0]?.id}/projects`;
  }
  return (
    <Flex mb={3} alignItems="center">
      {metIsLoading && (
        <Loading
          data-testid="loading-icon"
          w="4"
          h="4"
          mr="2"
          mt="-1px"
          alignItems="center"
        />
      )}
      {met && !metIsLoading && (
        <Box>
          <CheckIcon data-testid="check-icon" color="green" mr={2} mb={1} />
        </Box>
      )}
      {!met && !metIsLoading && (
        <Box>
          <CloseIcon
            data-testid="warning-icon"
            color="red"
            mr={2}
            boxSize={4}
            mb={2}
          />
        </Box>
      )}
      <Text mb={0}>
        {prereq.warning}{' '}
        {!metIsLoading && prereq.setup_path && !met && (
          <Link as={OrgRouteLink} to={prereq.setup_path} target="_blank">
            <Button variant="ghost" mb={1}>
              Set up now
            </Button>
          </Link>
        )}
      </Text>
    </Flex>
  );
};

Prerequisite.propTypes = {
  prereq: PropTypes.shape({
    warning: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
  }),
  integration: PropTypes.shape({
    connections: PropTypes.array,
  }),
};

export default Prerequisite;
